import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  cancelAccountInvitation,
  createAccountInvitation,
  fetchAccountInvitations,
  fetchAccountMembers,
  removeAccountMember,
} from '../../api/users';
import { useAuth } from '../../contexts/AuthContext';
import Input from '../../components/ui/Input';
import {
  canManageTeam,
  membershipRoleLabel,
} from '../../support/accountFunction';
import { getApiErrorMessage } from '../../utils/errors';

const ROLE_OPTIONS = [
  { value: 'member', label: 'Membro' },
  { value: 'admin', label: 'Administrador' },
];

function Team() {
  const { account, currentMembership, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [lastInviteLink, setLastInviteLink] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canManage = canManageTeam(currentMembership);

  const loadData = useCallback(async () => {
    if (!account?.id) {
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const [membersResponse, invitationsResponse] = await Promise.all([
        fetchAccountMembers(account.id),
        canManage ? fetchAccountInvitations(account.id) : Promise.resolve({ data: [] }),
      ]);
      setMembers(membersResponse.data || []);
      setInvitations(invitationsResponse.data || []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar a equipe.'));
    } finally {
      setIsLoading(false);
    }
  }, [account?.id, canManage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInvite = async (event) => {
    event.preventDefault();
    if (!account?.id) {
      return;
    }
    setIsSubmitting(true);
    setError('');
    setLastInviteLink('');
    try {
      const invitation = await createAccountInvitation(account.id, {
        email: email.trim(),
        role,
      });
      const link = `${window.location.origin}/invite/${invitation.token}`;
      setLastInviteLink(link);
      setEmail('');
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível enviar o convite.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMember = async (memberUserId) => {
    if (!account?.id || !window.confirm('Remover este membro do workspace?')) {
      return;
    }
    try {
      await removeAccountMember(account.id, memberUserId);
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível remover o membro.'));
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!account?.id) {
      return;
    }
    try {
      await cancelAccountInvitation(account.id, invitationId);
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível cancelar o convite.'));
    }
  };

  const copyInviteLink = async () => {
    if (!lastInviteLink) {
      return;
    }
    await navigator.clipboard.writeText(lastInviteLink);
  };

  if (!canManage) {
    return (
      <div className="surface-card p-6">
        <h1 className="text-xl font-semibold text-content-primary mb-2">Equipe</h1>
        <p className="text-content-muted text-sm">
          Você não tem permissão para gerenciar membros deste workspace.
        </p>
        <Link to="/agents" className="btn-secondary inline-flex mt-4">
          Voltar
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-content-primary">Equipe</h1>
        <p className="text-content-muted text-sm mt-1">
          Gerencie quem tem acesso ao workspace {account?.name}.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <section className="surface-card p-6 space-y-4">
        <h2 className="text-lg font-semibold text-content-primary">Convidar membro</h2>
        <form onSubmit={handleInvite} className="grid gap-4 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <Input
            id="invite_email"
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colega@empresa.com"
            required
          />
          <div>
            <label htmlFor="invite_role" className="block text-sm font-medium text-content-primary mb-1.5">
              Papel
            </label>
            <select
              id="invite_role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary disabled:opacity-50"
          >
            {isSubmitting ? 'Enviando...' : 'Convidar'}
          </button>
        </form>

        {lastInviteLink && (
          <div className="rounded-lg border border-forge-200 bg-forge-50 px-4 py-3 text-sm">
            <p className="text-content-primary mb-2">Link de convite gerado:</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <code className="flex-1 break-all text-xs bg-white rounded px-2 py-1 border">
                {lastInviteLink}
              </code>
              <button type="button" onClick={copyInviteLink} className="btn-secondary">
                Copiar link
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="surface-card p-6">
        <h2 className="text-lg font-semibold text-content-primary mb-4">Membros</h2>
        {isLoading ? (
          <p className="text-content-muted text-sm">Carregando...</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {members.map((member) => (
              <li key={member.id} className="py-3 flex items-center justify-between gap-4">
                <div>
                  <div className="font-medium text-content-primary">
                    {member.user?.name || member.user?.email}
                    {member.user_id === user?.id ? ' (você)' : ''}
                  </div>
                  <div className="text-sm text-content-muted">
                    {member.user?.email} · {membershipRoleLabel(member.role)}
                  </div>
                </div>
                {member.user_id !== user?.id && member.role !== 'owner' && (
                  <button
                    type="button"
                    onClick={() => handleRemoveMember(member.user_id)}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Remover
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {invitations.length > 0 && (
        <section className="surface-card p-6">
          <h2 className="text-lg font-semibold text-content-primary mb-4">Convites pendentes</h2>
          <ul className="divide-y divide-slate-100">
            {invitations.map((invitation) => (
              <li key={invitation.id} className="py-3 flex items-center justify-between gap-4">
                <div>
                  <div className="font-medium text-content-primary">{invitation.email}</div>
                  <div className="text-sm text-content-muted capitalize">{invitation.role}</div>
                </div>
                <button
                  type="button"
                  onClick={() => handleCancelInvitation(invitation.id)}
                  className="text-sm text-content-muted hover:text-content-primary"
                >
                  Cancelar
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export default Team;
